// packages/web/src/components/account/auth-login-form.tsx
/**
 * Login form — email + password, with magic-link alt path.
 * Calls authApi.login → store.login → close modal. Wrong creds show inline.
 */

import { useState } from 'react';
import { useT } from '../../i18n/use-t.js';
import { useStore } from '../../lib/store/index.js';
import { authApi } from '../../api/auth.js';
import { IngestApiError } from '../../lib/api-client.js';
import type { AuthMode } from './auth-modal.js';

interface Props {
  onSwitchMode: (m: AuthMode) => void;
  onClose: () => void;
}

export function AuthLoginForm({ onSwitchMode, onClose }: Props) {
  const t = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (loading) return;
    setError(null);
    setLoading(true);
    try {
      const result = await authApi.login(email.trim().toLowerCase(), password);
      useStore.getState().setCurrentUser(result.user);
      onClose();
    } catch (err) {
      if (err instanceof IngestApiError && err.status === 401) {
        setError(t('auth.loginFailed'));
      } else {
        setError(err instanceof Error ? err.message : 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="auth-head">
        <span className="auth-head-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
            <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        </span>
        <h2>{t('auth.welcome')}</h2>
      </div>
      <form className="auth-body" onSubmit={submit}>
        <div className="auth-field">
          <label className="auth-field-label" htmlFor="auth-login-email">
            <span>{t('auth.email')}</span>
          </label>
          <input
            id="auth-login-email"
            className={`auth-field-input${error ? ' has-error' : ''}`}
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoFocus
          />
        </div>
        <div className="auth-field">
          <label className="auth-field-label" htmlFor="auth-login-pw">
            <span>{t('auth.password')}</span>
            {/* 19BN Phase D D3: "forgot password" now opens the dedicated reset
                flow (forgot → reset email → resetPassword), not the magic-link
                login. Magic-link login is still reachable via the button below. */}
            <button type="button" className="auth-field-aux" onClick={() => onSwitchMode('forgot')}>
              {t('auth.forgotPassword')}
            </button>
          </label>
          <div className="auth-field-input-wrap">
            <input
              id="auth-login-pw"
              className={`auth-field-input${error ? ' has-error' : ''}`}
              type={showPw ? 'text' : 'password'}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <button
              type="button"
              className="auth-field-toggle-pw"
              onClick={() => setShowPw((v) => !v)}
              aria-label={showPw ? t('auth.hidePassword') : t('auth.viewPassword')}
            >
              {showPw ? (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                  <path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" />
                  <path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" />
                  <path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" />
                  <line x1="2" y1="2" x2="22" y2="22" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
                  <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
          {error && (
            <div className="auth-field-error">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {error}
            </div>
          )}
        </div>
        <button type="submit" className={`auth-btn-primary${loading ? ' is-loading' : ''}`} disabled={loading}>
          {t('auth.welcome')}
        </button>
        <div className="auth-divider">{t('auth.orDivider')}</div>
        <button type="button" className="auth-btn-secondary" onClick={() => onSwitchMode('magic')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="15" height="15">
            <rect width="20" height="16" x="2" y="4" rx="2" />
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
          </svg>
          {t('auth.magicLinkLogin')}
        </button>
        <div className="auth-switch">
          {t('auth.noAccount')}{' '}
          <button type="button" onClick={() => onSwitchMode('signup')}>{t('auth.signUp')} →</button>
        </div>
      </form>
    </>
  );
}
